import React from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

// Particle system for dual core effects
let particles = [];

const createParticle = (x, y, intensity) => {
  return {
    x: x,
    y: y,
    vx: (Math.random() - 0.5) * 4,
    vy: (Math.random() - 0.5) * 4 - 2,
    life: 1.0,
    decay: 0.02 + Math.random() * 0.02,
    size: 2 + Math.random() * 3,
    hue: 200 + Math.random() * 100, // Purple-blue range
    intensity: intensity / 255
  };
};

const updateParticles = () => {
  for (let i = particles.length - 1; i >= 0; i--) {
    const particle = particles[i];
    
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.life -= particle.decay;
    particle.vy += 0.1; // Gravity effect
    
    if (particle.life <= 0) {
      particles.splice(i, 1);
    }
  }
};

const drawParticles = (ctx) => {
  particles.forEach(particle => {
    const alpha = particle.life * particle.intensity;
    
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `hsla(${particle.hue}, 80%, 70%, ${alpha})`;
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
    ctx.fill();
    
    // Add glow effect
    ctx.shadowBlur = 10;
    ctx.shadowColor = `hsla(${particle.hue}, 80%, 70%, ${alpha * 0.5})`;
    ctx.fill();
    ctx.restore();
  });
};

export const drawDualCore = (ctx, dataArray, width, height, time) => {
  if (!ctx || !dataArray || width <= 0 || height <= 0) return;

  try {
    // Clear canvas with fade effect for trailing
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);

    // Create gradient background - purple theme
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width/2);
    gradient.addColorStop(0, 'rgba(168, 85, 247, 0.1)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Draw frequency bars at bottom using optimized approach (reduced bar count)
    const bufferLength = dataArray.length;
    const maxBars = Math.min(170, bufferLength); // Increased from 100 to 170 (34% reduction)
    const step = Math.floor(bufferLength / maxBars);
    const barWidth = width / maxBars * getResponsiveMultiplier(); // Use responsive multiplier
    let x = 0;

    for (let i = 0; i < maxBars; i++) {
      const dataIndex = Math.min(i * step, bufferLength - 1);
      // Reduced bar height multiplier to match other visualizers
      const barHeight = (dataArray[dataIndex] / 255) * height * 0.45;

      // Create bar gradient - purple-blue range
      const barGradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
      const hue = (i / bufferLength) * 300 + 200; // Purple-blue range
      barGradient.addColorStop(0, `hsla(${hue}, 80%, 50%, 0.8)`);
      barGradient.addColorStop(1, `hsla(${hue + 40}, 90%, 70%, 1)`);

      ctx.fillStyle = barGradient;
      ctx.fillRect(x, height - barHeight, barWidth, barHeight);

      x += barWidth + 1; // Small gap between bars
    }

    // Left circular visualizer
    const leftCenterX = width * 0.25;
    const leftCenterY = height * 0.4;
    const leftRadius = Math.min(width, height) * 0.12;

    // Draw left circle base
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(leftCenterX, leftCenterY, leftRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Add glow to left circle
    ctx.save();
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(168, 85, 247, 0.3)';
    ctx.stroke();
    ctx.restore();

    // Right circular visualizer
    const rightCenterX = width * 0.75;
    const rightCenterY = height * 0.4;
    const rightRadius = Math.min(width, height) * 0.12;

    // Draw right circle base
    ctx.strokeStyle = 'rgba(34, 197, 94, 0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(rightCenterX, rightCenterY, rightRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Add glow to right circle
    ctx.save();
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(34, 197, 94, 0.3)';
    ctx.stroke();
    ctx.restore();

    // Draw spokes for both circles
    for (let i = 0; i < bufferLength; i += 3) {
      const angle = (i / bufferLength) * Math.PI * 2 + time * 0.5;
      const amplitude = (dataArray[i] / 255) * 40;

      // Left circle spokes (purple)
      const leftX1 = leftCenterX + Math.cos(angle) * leftRadius;
      const leftY1 = leftCenterY + Math.sin(angle) * leftRadius;
      const leftX2 = leftCenterX + Math.cos(angle) * (leftRadius + amplitude);
      const leftY2 = leftCenterY + Math.sin(angle) * (leftRadius + amplitude);

      const leftHue = (i / bufferLength) * 60 + 260;
      const leftAlpha = amplitude / 40;
      
      ctx.strokeStyle = `hsla(${leftHue}, 80%, 60%, ${leftAlpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(leftX1, leftY1);
      ctx.lineTo(leftX2, leftY2);
      ctx.stroke();

      // Add glow to intense left spokes
      if (amplitude > 25) {
        ctx.save();
        ctx.shadowBlur = 10;
        ctx.shadowColor = `hsla(${leftHue}, 80%, 60%, ${leftAlpha * 0.5})`;
        ctx.stroke();
        ctx.restore();
      }

      // Right circle spokes (green)
      const rightX1 = rightCenterX + Math.cos(angle) * rightRadius;
      const rightY1 = rightCenterY + Math.sin(angle) * rightRadius;
      const rightX2 = rightCenterX + Math.cos(angle) * (rightRadius + amplitude);
      const rightY2 = rightCenterY + Math.sin(angle) * (rightRadius + amplitude);

      const rightHue = (i / bufferLength) * 60 + 120;
      
      ctx.strokeStyle = `hsla(${rightHue}, 80%, 60%, ${leftAlpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(rightX1, rightY1);
      ctx.lineTo(rightX2, rightY2);
      ctx.stroke();

      // Add glow to intense right spokes
      if (amplitude > 25) {
        ctx.save();
        ctx.shadowBlur = 10;
        ctx.shadowColor = `hsla(${rightHue}, 80%, 60%, ${leftAlpha * 0.5})`;
        ctx.stroke();
        ctx.restore();
      }

      // Create particles for intense spokes
      if (amplitude > 25 && Math.random() > 0.8) {
        particles.push(createParticle(leftX2, leftY2, dataArray[dataIndex]));
        particles.push(createParticle(rightX2, rightY2, dataArray[dataIndex]));
      }
    }

    // Connection between circles based on mid-range frequencies
    const connectionIntensity = dataArray.slice(Math.floor(bufferLength/3), Math.floor(bufferLength*2/3))
      .reduce((a, b) => a + b, 0) / Math.floor(bufferLength/3) / 255;
    
    if (connectionIntensity > 0.4) {
      const connectionAlpha = connectionIntensity * 0.6;
      const connectionWidth = connectionIntensity * 6;
      
      // Main connection line
      ctx.strokeStyle = `rgba(255, 255, 255, ${connectionAlpha})`;
      ctx.lineWidth = connectionWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(leftCenterX + leftRadius, leftCenterY);
      ctx.lineTo(rightCenterX - rightRadius, rightCenterY);
      ctx.stroke();

      // Connection glow
      ctx.save();
      ctx.shadowBlur = 20;
      ctx.shadowColor = `rgba(255, 255, 255, ${connectionAlpha * 0.5})`;
      ctx.stroke();
      ctx.restore();

      // Energy pulses along connection
      drawConnectionPulses(ctx, leftCenterX + leftRadius, leftCenterY, rightCenterX - rightRadius, rightCenterY, connectionIntensity, time);
    }

    // Update and draw particles
    updateParticles();
    drawParticles(ctx);

    // Add dual core effects
    drawDualCoreEffects(ctx, dataArray, width, height, time, leftCenterX, leftCenterY, rightCenterX, rightCenterY);

  } catch (error) {
    console.error('❌ Dual Core visualization error:', error);
  }
};

const drawConnectionPulses = (ctx, startX, startY, endX, endY, intensity, time) => {
  ctx.save();
  
  const pulseCount = 3;
  const connectionLength = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
  
  for (let p = 0; p < pulseCount; p++) {
    const pulseProgress = ((time * 2 + p * 0.5) % 1);
    const pulseX = startX + (endX - startX) * pulseProgress;
    const pulseY = startY + (endY - startY) * pulseProgress;
    const pulseSize = 8 + intensity * 12;
    const pulseAlpha = intensity * (1 - Math.abs(pulseProgress - 0.5) * 2);
    
    if (pulseAlpha > 0.1) {
      const pulseHue = 200 + p * 60;
      
      // Pulse core
      ctx.fillStyle = `hsla(${pulseHue}, 100%, 80%, ${pulseAlpha})`;
      ctx.beginPath();
      ctx.arc(pulseX, pulseY, pulseSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Pulse glow
      ctx.save();
      ctx.shadowBlur = 15;
      ctx.shadowColor = `hsla(${pulseHue}, 100%, 70%, ${pulseAlpha * 0.5})`;
      ctx.fill();
      ctx.restore();
    }
  }
  
  ctx.restore();
};

const drawDualCoreEffects = (ctx, dataArray, width, height, time, leftX, leftY, rightX, rightY) => {
  // Calculate average intensity for effects
  const avgIntensity = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
  const normalizedIntensity = avgIntensity / 255;

  // Draw energy fields around each core
  drawEnergyField(ctx, leftX, leftY, normalizedIntensity, time, 260); // Purple field
  drawEnergyField(ctx, rightX, rightY, normalizedIntensity, time, 140); // Green field

  // Draw data streams between cores
  if (normalizedIntensity > 0.5) {
    drawDataStreams(ctx, leftX, leftY, rightX, rightY, normalizedIntensity, time);
  }

  // Draw core pulsing effects
  drawCorePulse(ctx, leftX, leftY, normalizedIntensity, time, 260);
  drawCorePulse(ctx, rightX, rightY, normalizedIntensity, time, 140);
};

const drawEnergyField = (ctx, centerX, centerY, intensity, time, hue) => {
  ctx.save();
  
  const fieldRadius = 60 + intensity * 40;
  const fieldAlpha = intensity * 0.3;
  
  // Rotating energy field
  const rotation = time * 0.5;
  const segments = 12;
  
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2 + rotation;
    const segmentLength = Math.PI * 2 / segments * 0.8;
    
    ctx.strokeStyle = `hsla(${hue + i * 10}, 70%, 60%, ${fieldAlpha})`;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    ctx.beginPath();
    ctx.arc(centerX, centerY, fieldRadius, angle, angle + segmentLength);
    ctx.stroke();
  }
  
  ctx.setLineDash([]);
  ctx.restore();
};

const drawDataStreams = (ctx, leftX, leftY, rightX, rightY, intensity, time) => {
  ctx.save();
  
  const streamCount = 5;
  
  for (let i = 0; i < streamCount; i++) {
    const streamProgress = ((time * 3 + i * 0.2) % 1);
    const streamX = leftX + (rightX - leftX) * streamProgress;
    const streamY = leftY + (rightY - leftY) * streamProgress + Math.sin(streamProgress * Math.PI * 4) * 20;
    
    const streamSize = 3 + intensity * 4;
    const streamAlpha = intensity * Math.sin(streamProgress * Math.PI);
    const streamHue = 180 + i * 30;
    
    if (streamAlpha > 0.1) {
      ctx.fillStyle = `hsla(${streamHue}, 80%, 70%, ${streamAlpha})`;
      ctx.beginPath();
      ctx.arc(streamX, streamY, streamSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Stream trail
      const trailLength = 20;
      const trailX = streamX - (rightX - leftX) * 0.1;
      const trailY = streamY - (rightY - leftY) * 0.1;
      
      const gradient = ctx.createLinearGradient(streamX, streamY, trailX, trailY);
      gradient.addColorStop(0, `hsla(${streamHue}, 80%, 70%, ${streamAlpha})`);
      gradient.addColorStop(1, `hsla(${streamHue}, 80%, 70%, 0)`);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = streamSize;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(streamX, streamY);
      ctx.lineTo(trailX, trailY);
      ctx.stroke();
    }
  }
  
  ctx.restore();
};

const drawCorePulse = (ctx, centerX, centerY, intensity, time, hue) => {
  ctx.save();
  
  const pulseSize = 15 + intensity * 25 + Math.sin(time * 4) * 8;
  const pulseAlpha = 0.6 + intensity * 0.4;
  
  // Core gradient
  const gradient = ctx.createRadialGradient(
    centerX, centerY, 0,
    centerX, centerY, pulseSize
  );
  gradient.addColorStop(0, `hsla(${hue}, 100%, 90%, ${pulseAlpha})`);
  gradient.addColorStop(0.5, `hsla(${hue + 30}, 90%, 80%, ${pulseAlpha * 0.7})`);
  gradient.addColorStop(1, `hsla(${hue + 60}, 80%, 70%, 0)`);
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, pulseSize, 0, Math.PI * 2);
  ctx.fill();
  
  // Core glow
  ctx.save();
  ctx.shadowBlur = 20;
  ctx.shadowColor = `hsla(${hue}, 100%, 80%, ${pulseAlpha * 0.5})`;
  ctx.fill();
  ctx.restore();
  
  ctx.restore();
};

// Export the component function that matches the expected interface
export function DualCore({ ctx, dataArray, width, height, isPlaying }) {
  const time = Date.now() * 0.001; // Convert to seconds for smooth animation
  drawDualCore(ctx, dataArray, width, height, time);
}